import { FilterExpression, IdPathParameters, like, Pageable, SearchOperation } from '@infuse/api/common';
import { Permission as ApiPermission } from '@infuse/api/iam/permission';
import { useInfiniteQuery } from '@tanstack/vue-query';
import { last } from 'lodash';
import { computed, ref } from 'vue';
import { mapToDomain } from '@infuse/iam/permission/domain';

const createSearchFilter = (searchQuery: string | undefined): FilterExpression | undefined => {
  let filterOptions: FilterExpression[] = [];

  if (searchQuery !== undefined) {
    filterOptions = [
      ...filterOptions,
      ...searchQuery
        .toLowerCase()
        .split(' ')
        .filter((part: string): boolean => part !== undefined && part !== null && part !== '')
        .map((part: string): string => `%${part}%`)
        .map((part: string): FilterExpression => {
          return new FilterExpression()
            .or(like('lower(name)', part))
            .or(like('lower(accessType)', part))
            .or(like('lower(category)', part))
            .or(like('lower(objectType)', part));
        })
    ];
  }

  if (filterOptions.length === 0) {
    return;
  }
  return filterOptions.reduce((acc, cv) => acc.and(cv));
};

export function useGenericPermissions({
  apiFn,
  queryKeys,
  // The permissions are loaded in for granting and assigning to roles, paginated breaks these screens
  pageSize = 99999,
  onlyEnableWithParentId = false,
  initialParentId
}: {
  apiFn: SearchOperation<ApiPermission> | SearchOperation<ApiPermission, IdPathParameters<string>>;
  queryKeys: string[];
  pageSize?: number;
  onlyEnableWithParentId?: boolean;
  initialParentId?: string;
}) {
  const searchQuery = ref<string | undefined>();
  const fetchParentId = ref<string | undefined>(initialParentId);

  const query = useInfiniteQuery<Pageable<ApiPermission>>({
    initialPageParam: 0,
    queryKey: [...queryKeys, fetchParentId, searchQuery],
    enabled: () => (onlyEnableWithParentId ? !!fetchParentId.value : true),
    queryFn: async ({ pageParam }) => {
      return apiFn(
        {
          size: pageSize,
          page: Number(pageParam),
          filter: searchQuery.value ? createSearchFilter(searchQuery.value) : undefined,
          sort: [{ key: 'name' }]
        },
        undefined,
        fetchParentId.value ? { id: fetchParentId.value } : undefined
      );
    },
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.pageable.pageNumber + 1)
  });

  const permissions = computed(() => query.data.value?.pages.flatMap((page) => page.content.map(mapToDomain)) || []);
  const currentPage = computed(() => (query.data.value ? (last(query.data.value?.pageParams) as number) : 0));

  return {
    ...query,
    searchQuery,
    fetchParentId,
    currentPage,
    permissions
  };
}
